import * as React from 'react';

import { Button, ConsoleDialog } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideCheck } from 'lucide-react';

import { itemCreateAtom } from '../../../_graphql/mutationItemCreate';
import {
  $canSubmitItemForm,
  $itemForm,
  $resetItemForm,
} from '../../../_state/itemForm.state';
import { useTranslations } from '../../../i18n/useTranslations';

import { ItemFormFields } from './ItemFormFields';

interface ItemCreateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const ItemCreateDialog = ({
  isOpen,
  onClose,
  onSuccess,
}: ItemCreateDialogProps) => {
  const t = useTranslations();

  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const form = useAtomValue($itemForm);
  const canSubmit = useAtomValue($canSubmitItemForm);

  const resetForm = useSetAtom($resetItemForm);
  const createItem = useSetAtom(itemCreateAtom({}));

  const toast = useToast();

  React.useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  const handleClose = React.useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const handleSubmit = React.useCallback(async () => {
    if (!form.categoryId || !form.itemGroupId) {
      return;
    }

    try {
      setIsSubmitting(true);
      await createItem({
        name: form.name.trim(),
        skuNumber: form.skuNumber.trim(),
        categoryId: form.categoryId,
        itemGroupId: form.itemGroupId,
        description: form.description.trim(),
        notes: form.notes.trim(),
        measurementUnit: 'Pounds',
        pricingUnitOverride: form.pricingUnitOverride ?? null,
        attributeValues: {},
      });
      toast.success(t.inventoryView.itemCreateDialog.messages.success);
      onSuccess?.();
      handleClose();
    } catch (err) {
      toast.error(
        t.inventoryView.itemCreateDialog.messages.error(
          err instanceof Error ? err.message : 'Unknown error',
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [createItem, form, toast, onSuccess, handleClose, t]);

  return (
    <ConsoleDialog
      className="@ItemCreateDialog"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      title={t.inventoryView.itemCreateDialog.title}
      content={<ItemFormFields disabled={isSubmitting} />}
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            color="neutral"
            onPress={handleClose}
            isDisabled={isSubmitting}
          >
            {t.inventoryView.itemCreateDialog.actions.cancel}
          </Button>
          <Button
            icon={<LucideCheck />}
            color="primary"
            onPress={handleSubmit}
            isDisabled={!canSubmit || isSubmitting}
          >
            {isSubmitting
              ? t.inventoryView.itemCreateDialog.actions.creating
              : t.inventoryView.itemCreateDialog.actions.create}
          </Button>
        </div>
      }
    />
  );
};
